import { debounce } from 'lodash';
import { useEffect, useState } from 'react';
import { useRefState } from './useRefState';

export type UseIsOverflowingOptions = {
  /** Whether to recompute values when the window resizes */
  shouldUpdateOnResize?: boolean;
  /** The amount of time to wait after the window resizes before recomputing values. */
  resizeDebounceMs?: number;
  /** Whether to check for horizontal overflow */
  overflowX?: boolean;
  /** Whether to check for vertical overflow */
  overflowY?: boolean;
  /**
   * The amount of pixels to add to the offsetHeight to account for certain font scrollHeights being slightly off.
   * @see https://stackoverflow.com/questions/21064101/understanding-offsetwidth-clientwidth-scrollwidth-and-height-respectively
   * @see https://stackoverflow.com/a/52819747
   * @see https://github.com/rsms/inter/issues/602#issuecomment-1652505239
   */
  normalizeOffset?: number;
  /**
   * The value to use for maxViewableWidth when it is 0 or falsy.
   */
  defaultMaxViewableWidth?: number;
};

export const USE_IS_OVERFLOWING_DEFAULT_OPTIONS = {
  shouldUpdateOnResize: true,
  resizeDebounceMs: 100,
  overflowX: true,
  overflowY: false,
  normalizeOffset: 0,
  defaultMaxViewableWidth: 0,
} as const;

/**
 * Returns whether a given html element is overflowing its visible width.
 *
 * @param dependencies
 * @param options
 * @returns
 */
export const useIsOverflowing = <TextHTMLElement extends HTMLElement = HTMLElement>(
  options: UseIsOverflowingOptions = USE_IS_OVERFLOWING_DEFAULT_OPTIONS,
) => {
  const elementRef = useRefState<TextHTMLElement | null>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [maxViewableWidth, setMaxViewableWidth] = useState(
    () =>
      options.defaultMaxViewableWidth ?? USE_IS_OVERFLOWING_DEFAULT_OPTIONS.defaultMaxViewableWidth,
  );

  useEffect(() => {
    const _options = { ...USE_IS_OVERFLOWING_DEFAULT_OPTIONS, ...options };

    const element = elementRef?.current;

    if (!element) return;

    const debounced = debounce(() => {
      const scrollWidth = elementRef?.current?.scrollWidth ?? 0;
      const scrollHeight = elementRef?.current?.scrollHeight ?? 0;

      const offsetWidth = elementRef?.current?.offsetWidth ?? 0;
      const offsetHeight = elementRef?.current?.offsetHeight ?? 0;

      const overflowing = !!(
        (_options.overflowX && scrollWidth > offsetWidth) ||
        (_options.overflowY && scrollHeight > offsetHeight + _options.normalizeOffset)
      );

      setIsOverflowing(overflowing);
      if (!offsetWidth) {
        setMaxViewableWidth(
          _options.defaultMaxViewableWidth ??
            USE_IS_OVERFLOWING_DEFAULT_OPTIONS.defaultMaxViewableWidth,
        );
      } else {
        setMaxViewableWidth(offsetWidth);
      }
    }, _options.resizeDebounceMs);

    debounced();

    if (_options?.shouldUpdateOnResize) {
      const observer = new ResizeObserver(debounced);
      observer.observe(element);

      return () => {
        debounced.cancel();
        observer.disconnect();
      };
    }

    return () => {
      debounced.cancel();
    };
  }, [elementRef, setIsOverflowing]);

  return {
    isOverflowing,
    elementRef,
    maxViewableWidth,
  };
};
